import React, { useState, useEffect, useCallback, useRef } from 'react';
import { searchShifts, getEmployees, getEmployeeTypeDisplayName, getHospitals, bulkImportShifts, BulkImportShiftsResult, wipeShifts } from '../services/api.ts';
import { utcToLocalTime } from '../utils/timezone.ts';
import { localDateToUtcStart, localDateToUtcEnd } from '../utils/timezone.ts';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import { compareShiftRowsByTypeThenEnd } from './boardViewHelpers.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

interface ShiftsCardProps {
  selectedDate: string;
  hasPersistedAllocations: boolean;
  showSearch?: boolean;
  showUpload?: boolean; // Control whether to show the Upload Spreadsheet button
  // Optional: when provided (Board usage), filter shifts to those covering this instant
  targetTimeUtc?: string;
  // Employees currently allocated at target time
  busyDoctors?: string[];
  busyNonDoctors?: string[];
  // Key to force refresh when changed
  refreshKey?: number;
  // 'available' (default) hides shifts not covering targetTimeUtc and hides busy non-doctors;
  // 'all' shows every shift for the selected day regardless of target time / busy state.
  filterMode?: 'available' | 'all';
  // Header text. Defaults to 'Available Shifts'.
  title?: string;
  // When provided, use these shifts directly and skip the internal fetch.
  // Lets a parent (e.g. BoardView) own the shift data so multiple consumers share one fetch.
  shifts?: any[];
  // Forwarded loading flag for the parent-owned-shifts case. Ignored when `shifts` is undefined.
  loading?: boolean;
  // When true, render a destructive "Delete All Shifts" button that wipes every shift overlapping
  // the selected day. Defaults to false so reuse contexts (e.g. BoardView) never expose it.
  showDeleteAll?: boolean;
}

const ShiftsCard: React.FC<ShiftsCardProps> = ({ selectedDate, hasPersistedAllocations, showSearch = true, showUpload = true, targetTimeUtc, busyDoctors = [], busyNonDoctors = [], refreshKey, filterMode = 'available', title = 'Available Shifts', shifts: shiftsProp, loading: loadingProp, showDeleteAll = false }) => {
  const { isAdmin } = useAuth();
  const externallyProvided = shiftsProp !== undefined;
  const [internalShifts, setInternalShifts] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const shifts = externallyProvided ? (shiftsProp as any[]) : internalShifts;
  const isLoading = externallyProvided ? (loadingProp ?? false) : loadingShifts;
  const [filterEmployee, setFilterEmployee] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkImportShiftsResult | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadHospitals = async () => {
    try {
      const response = await getHospitals();
      setHospitals(response.hospitals || []);
    } catch (error) {
      console.error('Error loading hospitals:', error);
    }
  };

  const search = useCallback(async () => {
    if (!selectedDate || externallyProvided) return;

    setLoadingShifts(true);
    try {
      const startTimeUtc = localDateToUtcStart(selectedDate);
      const endTimeUtc = localDateToUtcEnd(selectedDate);
      const response = await searchShifts(startTimeUtc, endTimeUtc);
      setInternalShifts(response.shifts || []);
    } catch (error) {
      console.error('Error searching shifts:', error);
    } finally {
      setLoadingShifts(false);
    }
  }, [selectedDate, externallyProvided]);

  useEffect(() => {
    loadEmployees();
    loadHospitals();
  }, []);

  useEffect(() => {
    if (selectedDate && !externallyProvided) {
      search();
    }
  }, [selectedDate, search, externallyProvided]);

  // Refresh shifts when refreshKey changes (e.g., after simulation)
  useEffect(() => {
    if (selectedDate && refreshKey !== undefined && !externallyProvided) {
      search();
    }
  }, [refreshKey, selectedDate, search, externallyProvided]);

  const coversTarget = (shift: any) => {
    if (!targetTimeUtc) return true;
    const t = new Date(targetTimeUtc).getTime();
    const s = new Date(shift.startTimeUtc).getTime();
    const e = new Date(shift.endTimeUtc).getTime();
    return t >= s && t < e;
  };

  const isBusyAndHidden = (shift: any) => {
    const empId = shift.employeeId as string;
    if (busyNonDoctors.includes(empId)) return true; // hide nurses and students when allocated
    return false;
  };

  const filteredShifts = shifts
    .filter(shift => filterMode === 'all' ? true : coversTarget(shift))
    .filter(shift => {
      if (filterMode !== 'all' && isBusyAndHidden(shift)) return false;
      if (!showSearch || !filterEmployee) return true;
      const employee = employees.find(emp => emp.employeeId === shift.employeeId);
      if (!employee) return false;
      const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
      return fullName.includes(filterEmployee.toLowerCase());
    })
    .sort(compareShiftRowsByTypeThenEnd);

  const formatTime = (utcTime: string) => {
    return utcToLocalTime(utcTime);
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.employeeId === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  const getEmployeeById = (employeeId: string) => {
    return employees.find(emp => emp.employeeId === employeeId);
  };

  const getHospitalName = (hospitalId: string) => {
    const hospital = hospitals.find(h => h.hospitalId === hospitalId);
    return hospital ? hospital.name : undefined;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|ods)$/i)) {
      alert('Please upload a valid Excel file (.xls, .xlsx, or .ods)');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target?.result as string;
          // Remove the data URL prefix (e.g., "data:application/vnd.ms-excel;base64,")
          const base64Content = base64Data.split(',')[1];

          // Call the API
          const result = await bulkImportShifts(base64Content);
          setUploadResult(result);

          // Refresh shifts list if any were created
          if (result.shiftsCreated > 0) {
            await search();
          }

          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Error importing shifts:', error);
          alert('Failed to import shifts. Please check the console for details.');
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        alert('Failed to read the file. Please try again.');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Failed to process the file. Please try again.');
      setUploading(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteAll = async () => {
    if (!selectedDate) return;
    if (!window.confirm(`Are you sure you want to delete all shifts for ${selectedDate}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setDeleteResult(null);
    try {
      const result = await wipeShifts(selectedDate);
      setDeleteResult(result.message);
      if (!externallyProvided) {
        await search();
      }
    } catch (error) {
      console.error('Error deleting shifts:', error);
      window.alert('Failed to delete shifts. Please check the console for details.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isAdmin && showUpload && (
            <>
              <button
                onClick={handleUploadButtonClick}
                disabled={uploading || !selectedDate}
                style={{
                  padding: '6px 12px',
                  fontSize: '0.85em',
                  backgroundColor: uploading ? 'var(--bg-surface-hover)' : 'var(--success)',
                  color: uploading ? 'var(--text-muted)' : 'var(--labcoat)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: uploading || !selectedDate ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!uploading && selectedDate) {
                    e.currentTarget.style.backgroundColor = '#128a3e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploading && selectedDate) {
                    e.currentTarget.style.backgroundColor = 'var(--success)';
                  }
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Spreadsheet'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.ods"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </>
          )}
          {isAdmin && showDeleteAll && !externallyProvided && (
            <button
              onClick={handleDeleteAll}
              disabled={deleting || !selectedDate || filteredShifts.length === 0}
              aria-label="Delete All Shifts"
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                backgroundColor: deleting || !selectedDate || filteredShifts.length === 0 ? 'var(--bg-surface-hover)' : 'var(--danger)',
                color: deleting || !selectedDate || filteredShifts.length === 0 ? 'var(--text-muted)' : 'var(--labcoat)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: deleting || !selectedDate || filteredShifts.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!deleting && selectedDate && filteredShifts.length > 0) {
                  e.currentTarget.style.backgroundColor = '#b8412d';
                }
              }}
              onMouseLeave={(e) => {
                if (!deleting && selectedDate && filteredShifts.length > 0) {
                  e.currentTarget.style.backgroundColor = 'var(--danger)';
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Delete All Shifts'}
            </button>
          )}
          <span style={{
            fontSize: '0.9em',
            color: 'var(--text-secondary)',
            padding: '4px 8px',
            backgroundColor: 'var(--bg-input)',
            borderRadius: '12px'
          }}>
            {filteredShifts.length} shift{filteredShifts.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {uploadResult && (
        <div style={{
          marginBottom: '15px',
          padding: '12px',
          borderRadius: '6px',
          backgroundColor: uploadResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.18)' : 'rgba(217, 119, 6, 0.18)',
          border: `1px solid ${uploadResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.4)' : 'rgba(217, 119, 6, 0.4)'}`,
          color: uploadResult.errors.length === 0 ? 'var(--success-text)' : 'var(--warning-text)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {uploadResult.message}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <div>Created: {uploadResult.shiftsCreated} shifts</div>
            <div>Parsed: {uploadResult.totalParsed} shifts</div>
            {uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Errors ({uploadResult.errors.length}):</strong>
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  {uploadResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx} style={{ fontSize: '0.85em' }}>
                      Cell {err.location}: {err.name} ({err.window}) - {err.error}
                    </li>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <li style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                      ... and {uploadResult.errors.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <button
            onClick={() => setUploadResult(null)}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '0.8em',
              backgroundColor: 'transparent',
              color: 'inherit',
              border: '1px solid currentColor',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {deleteResult && (
        <div
          role="status"
          style={{
            marginBottom: '15px',
            padding: '12px',
            borderRadius: '6px',
            backgroundColor: 'var(--success-soft)',
            border: '1px solid rgba(22, 163, 74, 0.4)',
            color: 'var(--success-text)'
          }}
        >
          <div style={{ fontWeight: 'bold' }}>{deleteResult}</div>
          <button
            onClick={() => setDeleteResult(null)}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '0.8em',
              backgroundColor: 'transparent',
              color: 'inherit',
              border: '1px solid currentColor',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {isLoading ? (
        <p style={{ margin: '10px 0', fontStyle: 'italic', color: 'var(--text-secondary)' }}>Loading shifts...</p>
      ) : (
        <>
          {showSearch && (
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <input
                type="text"
                id="filterEmployee"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                placeholder="🔍 Search by employee name..."
                className="form-control"
                style={{ fontSize: '0.9em' }}
              />
            </div>
          )}
          
          <div className="shifts-table" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table style={{ fontSize: '0.85em' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px 4px' }}>Employee</th>
                  <th style={{ padding: '8px 4px' }}>Type</th>
                  <th style={{ padding: '8px 4px' }}>Home Hospital</th>
                  <th style={{ padding: '8px 4px' }}>Start</th>
                  <th style={{ padding: '8px 4px' }}>End</th>
                  {filteredShifts.some(shift => shift.employeeType === 'doctor' && shift.order) && (
                    <th style={{ padding: '8px 4px' }}>Order</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredShifts.map((shift, index) => {
                  const isBusyDoctor = busyDoctors.includes(shift.employeeId);
                  const nameStyle = isBusyDoctor ? { color: '#d97706', fontWeight: 600 } : {};
                  const employee = getEmployeeById(shift.employeeId);

                  return (
                    <tr key={index}>
                      <td style={{ padding: '6px 4px', fontWeight: '500', ...nameStyle }}>
                        {employee ? (
                          <EmployeeDetailsPopover
                            employee={employee}
                            hospitalName={employee.homeHospitalId ? getHospitalName(employee.homeHospitalId) : undefined}
                            shift={{
                              startTimeUtc: shift.startTimeUtc,
                              endTimeUtc: shift.endTimeUtc,
                              order: shift.order
                            }}
                          >
                            {getEmployeeName(shift.employeeId)}
                          </EmployeeDetailsPopover>
                        ) : (
                          getEmployeeName(shift.employeeId)
                        )}
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <span style={{
                          fontSize: '0.8em',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          backgroundColor: shift.employeeType === 'doctor' ? 'rgba(74, 159, 212, 0.16)' :
                                         shift.employeeType === 'nurse' ? 'rgba(217, 119, 6, 0.16)' : 'rgba(22, 163, 74, 0.16)',
                          color: shift.employeeType === 'doctor' ? 'var(--or-blue-mid)' :
                                 shift.employeeType === 'nurse' ? 'var(--amber)' : 'var(--success-text)'
                        }}>
                          {getEmployeeTypeDisplayName(shift.employeeType)}
                        </span>
                      </td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>
                        {employee?.homeHospitalId ? (getHospitalName(employee.homeHospitalId) ?? '—') : '—'}
                      </td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>{formatTime(shift.startTimeUtc)}</td>
                      <td style={{ padding: '6px 4px', fontSize: '0.8em' }}>{formatTime(shift.endTimeUtc)}</td>
                      {shift.employeeType === 'doctor' && shift.order && (
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <span style={{
                            backgroundColor: 'rgba(217, 119, 6, 0.18)',
                            color: 'var(--amber)',
                            padding: '2px 6px',
                            borderRadius: '50%',
                            fontWeight: 'bold',
                            fontSize: '0.8em'
                          }}>
                            {shift.order}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ShiftsCard;
